import { EditIcon, EmptyState } from '@bp/ui-components';
import { BpCard } from 'components/BpCard/BpCard';
import { FileListDownloader } from 'components/Files/components/FileList/FileListDownloader';
import { useSubmissions } from 'components/Submissions/hooks/useSubmissions';
import { TipTapTextExtender } from 'components/TipTap/TipTapTextExtender';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';

type SubmissionFeedbackCardProps = { submissionUuid: string; onFeedback?: () => void; className?: string | undefined };

export const SubmissionFeedbackCard = ({ submissionUuid, onFeedback, className }: SubmissionFeedbackCardProps) => {
  const { t } = useTranslation();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);

  const { submissions } = useSubmissions({ courseUuid, assignmentUuid, submissionUuid });

  const submission = submissions.find((s) => s.uuid === submissionUuid);
  if (!submission) throw new Error('Submission not found');

  const feedback = submission.feedbackMaterial;

  return (
    <BpCard
      noPadding={feedback?.fileEntries && feedback.fileEntries.length > 0}
      alwaysCollapsible
      header={{
        headline: t('submissions.feedback'),
        actions: onFeedback
          ? [
              {
                text: t('submissions.editFeedback'),
                callback: () => onFeedback(),
                icon: <EditIcon />,
                hierarchy: 'tertiary',
              },
            ]
          : undefined,
      }}
      className={className}
    >
      {!feedback ? (
        <EmptyState size='small' subtitle={t('submissions.noFeedback')} hideIcon padding='l' />
      ) : (
        <>
          <TipTapTextExtender
            uuid={feedback.uuid}
            content={feedback.text ?? ''}
            padding={feedback.fileEntries.length === 0 ? 'none' : 'default'}
          />
          {feedback.fileEntries.length > 0 && <FileListDownloader files={feedback.fileEntries} className='mt-7' />}
        </>
      )}
    </BpCard>
  );
};
